import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, catchError, mergeMap, of, switchMap } from 'rxjs';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { ExternalResponse } from 'src/app/models/external-response';
import { ExternalsParametersService } from 'src/app/services/externals-parameters.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { SecurityPolicyService } from 'src/app/services/security-policy-service';
import { EnumModeResetPassword } from 'src/app/models/enum-mode-reset-password';
import { EnumReasonResetPassword } from 'src/app/models/enum-reason-reset-password';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-forgotten-password',
    templateUrl: './forgotten-password.component.html',
    styleUrls: ['./forgotten-password.component.css'],
    imports: [MatCardModule, TranslateModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatButton],
})
export class ForgottenPasswordComponent implements OnInit, OnDestroy {
    externalParameter: ExternalParameters;
    externalParametersChange: Subscription;
    forgottenPasswordForm: UntypedFormGroup;

    constructor(
        private externalsParametersService: ExternalsParametersService,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private securityPolicyService: SecurityPolicyService,
        private toastr: ToastrService,
        private translate: TranslateService
    ) {}

    ngOnDestroy(): void {
        this.externalsParametersService.resetExternalParameters();
        this.externalParametersChange.unsubscribe();
    }

    ngOnInit(): void {
        this.externalParametersChange = this.externalsParametersService.externalParametersChange$.subscribe((parameters) => {
            if (parameters) {
                this.externalParameter = parameters;
            }
        });

        this.forgottenPasswordForm = this.formBuilder.group({
            login: [
                '',
                [
                    Validators.required,
                    Validators.pattern(
                        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    ),
                ],
            ],
        });
    }

    onSubmit() {
        this.securityPolicyService
            .GetUserIdByLogin(this.forgottenPasswordForm.get('login').value)
            .pipe(
                mergeMap((userId) => {
                    if (userId) {
                        let returnUrl = `${window.location.origin}/changepassword?resetPassword=${userId}`;

                        if (this.externalParameter) {
                            returnUrl = encodeURIComponent(`${returnUrl}&parameters=${encodeURIComponent(JSON.stringify(this.externalParameter))}`);
                        }

                        return this.securityPolicyService.StartPasswordReset(userId, EnumReasonResetPassword.Loose, EnumModeResetPassword.Mail, returnUrl, null, false);
                    } else {
                        return of(userId);
                    }
                })
            )
            .subscribe({
                next: (x) => {
                    if (this.externalParameter?.return) {
                        const response = new ExternalResponse();
                        response.code = 200;
                        response.message = encodeURI(
                            this.translate.instant('ForgottenPassword.ResetMessage', { email: this.forgottenPasswordForm.get('login').value }).replace(/\"/g, '\\"')
                        );
                        window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                        this.externalsParametersService.resetExternalParameters();
                    } else {
                        this.toastr.success(this.translate.instant('ForgottenPassword.ResetMessage', { email: this.forgottenPasswordForm.get('login').value }), '', {
                            enableHtml: true,
                        });
                        this.router.navigateByUrl('/login');
                    }
                },
                error: (err: unknown) => {
                    const error = err as HttpErrorResponse;

                    if (error.status === 409) {
                        if (this.externalParameter?.return) {
                            const response = new ExternalResponse();
                            response.code = error.status;
                            response.message = encodeURI(this.translate.instant('ForgottenPassword.ResetMicrosoftAccountError'));
                            window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                            this.externalsParametersService.resetExternalParameters();
                        } else {
                        }

                        this.toastr.warning(this.translate.instant('ForgottenPassword.ResetMicrosoftAccountError'));
                    } else {
                        if (this.externalParameter?.return) {
                            const response = new ExternalResponse();
                            response.code = error.status;
                            response.message = encodeURI(this.translate.instant('ForgottenPassword.ResetError'));
                            window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                            this.externalsParametersService.resetExternalParameters();
                        } else {
                        }

                        this.toastr.error(this.translate.instant('ForgottenPassword.ResetError'));
                    }
                },
            });
    }

    cancel() {
        if (this.externalParameter?.return) {
            window.location.href = this.externalParameter.return;
            this.externalsParametersService.resetExternalParameters();
        } else {
            this.router.navigateByUrl('/login');
        }
    }
}
